/**
 * @fileoverview Sticky stepper for an in-progress department-wide occupancy migration.
 * @module system-web/components/migration/MigrationProgressSidebar
 *
 * Responsibilities:
 * - Render effective steps[] from the Dexie-backed parent plus latest dataset reruns.
 * - Stay visible while the user switches Occupancies ↔ Tanks on LegacyMigrationPage.
 * - Show live N / total, extract wait text, and a stale-heartbeat warning.
 *
 * Dependencies:
 * - lucide-react
 * - MigrationRunRow from legacyMigrationService
 * - groupDepartmentWideStepsByTier / formatStepProgressLine from departmentWideStepper
 *
 * Architecture:
 * - Presentational. Parent supplies run from useDepartmentWideRun (liveQuery).
 *
 * @component MigrationProgressSidebar
 * Purpose: Department-wide progress stepper on system-web migration page.
 * Props: run — active department-wide MigrationRunRow or null (renders nothing).
 *        liveJob — optional background_jobs overlay with Occupancies n / total.
 *        onViewReport — open the dedicated run report (does not expand steps over the grid).
 * Behavior: Compact header so the Occupancies grid keeps scroll height. Step cards stay collapsed
 *           unless the operator opens them; completed runs link to the report page.
 * Data Source: local (Dexie via hook)
 * Constraints: no apiClient
 * Performance: steps typically ≤ 15; 15s tick only for stale detection
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, XCircle, Circle, MinusCircle, AlertTriangle } from 'lucide-react';
import {
  departmentRunActivity,
  describeRunOutcome,
  formatRehostProgressLine,
  formatStepProgressLine,
  isDepartmentDatasetClear,
  groupDepartmentWideStepsByTier,
  isCompletedWithRowFailures,
  isDepartmentWideRunLive,
  isDepartmentWideRunStale,
  isStepHeartbeatStale,
} from '../../services/departmentWideStepper';
import type { DepartmentWideLiveJob, MigrationRunRow, MigrationRunStepRow } from '../../services/legacyMigrationService';

function StepIcon({ step, stale }: { step: MigrationRunStepRow; stale: boolean }) {
  if (isCompletedWithRowFailures(step)) return <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />;
  if (step.status === 'completed') return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />;
  if (step.status === 'running' && stale) return <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />;
  if (step.status === 'running') return <Loader2 className="h-4 w-4 text-orange-600 animate-spin shrink-0" />;
  if (step.status === 'failed') return <XCircle className="h-4 w-4 text-red-600 shrink-0" />;
  if (step.status === 'skipped') return <MinusCircle className="h-4 w-4 text-gray-400 shrink-0" />;
  return <Circle className="h-4 w-4 text-gray-300 shrink-0" />;
}

function RunningStepDetail({ step, nowMs }: { step: MigrationRunStepRow; nowMs: number }) {
  const line = formatStepProgressLine(step);
  const stale = isStepHeartbeatStale(step.heartbeatAt, nowMs, undefined, step.startedAt);
  return (
    <>
      <div className="text-[10px] text-orange-700">
        {step.percent}%{line ? ` · ${line}` : ''}
      </div>
      {step.total != null && step.total > 0 && (
        <div className="mt-1 h-1 rounded bg-orange-100 overflow-hidden">
          <div
            className="h-full bg-orange-500 transition-all"
            style={{ width: `${Math.min(100, Math.round(((step.processed ?? 0) / step.total) * 100))}%` }}
          />
        </div>
      )}
      {stale && (
        <div className="text-[10px] text-amber-700 mt-0.5">
          This migration has not reported progress for over 2 minutes. It may still be processing a large record.
        </div>
      )}
    </>
  );
}

export function MigrationProgressSidebar({
  run,
  liveJob = null,
  onStop,
  onResume,
  onViewReport,
  stopping = false,
}: {
  run: MigrationRunRow | null;
  liveJob?: DepartmentWideLiveJob | null;
  onStop?: () => void;
  onResume?: () => void;
  onViewReport?: () => void;
  stopping?: boolean;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasRunning = Boolean(run?.steps?.some((s) => s.status === 'running') || isDepartmentWideRunLive(run?.status));

  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [hasRunning]);

  const tiers = useMemo(
    () => groupDepartmentWideStepsByTier(run?.steps ?? []),
    [run?.steps],
  );

  if (!run || !run.steps || run.steps.length === 0) return null;

  const runningStep = run.steps.find((s) => s.status === 'running');
  const failedStep = run.steps.find((s) => s.status === 'failed');
  const stepLine = runningStep ? formatStepProgressLine(runningStep) : '';
  const jobCount = liveJob?.current;
  const jobIsCurrent = liveJob?.updatedAt
    && (!runningStep?.heartbeatAt || liveJob.updatedAt >= runningStep.heartbeatAt)
    && (jobCount == null || jobCount === runningStep?.processed);
  const liveLine = jobIsCurrent ? liveJob?.message?.trim() || '' : '';
  const runningLine = liveLine || stepLine;
  const headerPercent = (jobIsCurrent ? liveJob?.percent : undefined) ?? run.overallPercent ?? 0;
  const activity = departmentRunActivity(run, nowMs);
  const stale = activity === 'paused' || activity === 'stalled';
  const stoppingNow = Boolean(stopping || run.cancelRequested) && isDepartmentWideRunLive(run.status);
  const canStop = isDepartmentWideRunLive(run.status) && !stoppingNow;
  const canResume = run.status === 'cancelled' || run.status === 'failed' || activity === 'paused';
  const needsAttention = !hasRunning && (run.counts.failed > 0 || run.steps.some((step) => !isDepartmentDatasetClear(step)));
  const displayStatus = stoppingNow
    ? 'stopping'
    : activity === 'stalled'
      ? 'stalled — no new record'
      : activity === 'paused'
        ? 'paused — worker stopped'
        : activity === 'restarting'
          ? 'restarted — waiting'
          : hasRunning ? 'running' : needsAttention ? 'needs attention' : run.status;
  const clearDatasetCount = run.steps.filter((step) => isDepartmentDatasetClear(step)).length;
  const latestAttemptStep = run.steps
    .filter((step) => step.overlayRunId)
    .sort((a, b) => String(b.overlayStartedAt ?? '').localeCompare(String(a.overlayStartedAt ?? '')))[0];

  return (
    <section className="shrink-0 border-b bg-orange-50/70" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <div className="px-4 py-2.5 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900">Department-wide migration</h2>
            <span className="text-xs text-gray-600">
              {headerPercent}% · {run.mode === 'dry-run' ? 'Dry run' : 'Apply'} · {displayStatus}
            </span>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            Department run <span className="font-mono">{run._id}</span>
            {latestAttemptStep?.overlayRunId ? (
              <> · Latest dataset attempt <span className="font-mono">{latestAttemptStep.overlayRunId}</span> ({latestAttemptStep.displayName})</>
            ) : null}
          </div>
          <div className="mt-1.5 h-1.5 rounded bg-white overflow-hidden border border-orange-100">
            <div
              className="h-full bg-orange-500 transition-all"
              style={{ width: `${Math.min(100, headerPercent)}%` }}
            />
          </div>
          {(runningStep || liveLine) && (
            <p className="text-xs text-orange-800 mt-1.5 truncate">
              {liveLine || `${runningStep?.displayName}: ${runningLine || `${runningStep?.percent ?? 0}%`}`}
            </p>
          )}
          {activity === 'stalled' && (
            <p className="text-[11px] text-amber-800 mt-1">
              Stalled. No new record has been saved for over 5 minutes. The worker may still be running, so do not start another migration. Choose Stop, then Resume remaining.
            </p>
          )}
          {activity === 'paused' && (
            <p className="text-[11px] text-amber-800 mt-1">
              Worker stopped. Last saved record is shown above. Choose Resume remaining to continue the same run. Do not start a new department migration.
            </p>
          )}
          {activity === 'restarting' && (
            <p className="text-[11px] text-gray-700 mt-1">
              A worker restarted and has not saved the next record yet. Completed datasets are kept. Do not start another migration.
            </p>
          )}
          {failedStep && (failedStep.error || failedStep.message) && (
            <p className="text-[11px] text-red-700 mt-1 truncate">
              {failedStep.displayName} failed: {failedStep.error || failedStep.message}
            </p>
          )}
          {stoppingNow && (
            <p className="text-[11px] text-gray-700 mt-1">
              Stop requested. Occupancies SQL extract may finish before remaining datasets are skipped.
            </p>
          )}
          {run.status === 'cancelled' && (
            <p className="text-[11px] text-gray-700 mt-1">Stopped. Resume continues remaining occupancy datasets.</p>
          )}
          {(run.status === 'completed' || run.status === 'failed') && (
            <p className="text-[11px] text-gray-700 mt-1">
              {clearDatasetCount} / {run.steps.length} datasets clear
              {run.counts.failed > 0 ? ` · ${run.counts.failed} failed rows remain` : ' · no failed rows remain'}
              {(() => {
                const docStep = (run.steps ?? []).find((s) => s.adapterKey === 'fileMetadata');
                const hasDocumentRows = docStep
                  ? ((docStep.counts?.selected ?? 0) > 0 || (docStep.counts?.created ?? 0) > 0 || (docStep.counts?.updated ?? 0) > 0)
                  : undefined;
                const copyLine = formatRehostProgressLine({
                  rehostStatus: run.rehostStatus,
                  rehostCounts: run.rehostCounts,
                  hasDocumentRows,
                });
                return copyLine ? ` · ${copyLine}` : '';
              })()}
              . Open the dashboard for dataset and attempt details.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(canStop || stoppingNow) && onStop && (
            <button
              type="button"
              onClick={onStop}
              disabled={stoppingNow}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-red-200 text-red-700 bg-white rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              {stoppingNow ? 'Stopping department…' : 'Stop department'}
            </button>
          )}
          {canResume && onResume && (
            <button
              type="button"
              onClick={onResume}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-orange-200 text-orange-800 bg-white rounded-md hover:bg-orange-50"
            >
              Resume department
            </button>
          )}
          {onViewReport && (
            <button
              type="button"
              onClick={onViewReport}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-orange-200 text-orange-800 bg-white rounded-md hover:bg-orange-50"
            >
              View report
            </button>
          )}
          <button
            type="button"
            onClick={() => setDetailsOpen((open) => !open)}
            className="text-xs text-gray-600 hover:text-gray-900"
          >
            {detailsOpen ? 'Hide steps' : 'Show steps'}
          </button>
        </div>
      </div>
      {detailsOpen && (
        <ol className="px-3 pb-3 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {tiers.flatMap((tier) =>
            tier.steps.map((step) => {
              const stepStale = step.status === 'running' && isStepHeartbeatStale(step.heartbeatAt, nowMs, undefined, step.startedAt);
              return (
                <li
                  key={step.adapterKey}
                  className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs bg-white border ${
                    step.status === 'running' ? (stepStale ? 'border-amber-300' : 'border-orange-300') : 'border-transparent'
                  }`}
                >
                  <StepIcon step={step} stale={stepStale} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800 truncate">{step.displayName}</div>
                    {step.status === 'running' && <RunningStepDetail step={step} nowMs={nowMs} />}
                    {step.status === 'failed' && (step.error || step.message) && (
                      <div className="text-[10px] text-red-600 truncate">{step.error || step.message}</div>
                    )}
                    {step.status === 'completed' && (
                      <div className={`text-[10px] ${isCompletedWithRowFailures(step) ? 'text-amber-700' : 'text-gray-500'}`}>
                        {step.counts.created} created · {step.counts.updated} updated
                        {step.counts.failed > 0 ? ` · ${step.counts.failed} failed` : ''}
                      </div>
                    )}
                  </div>
                </li>
              );
            }),
          )}
        </ol>
      )}
    </section>
  );
}

/**
 * @component LatestDepartmentStatusChip
 * Purpose: One-line latest department-wide status when no run is in progress.
 * Props: run, onViewReport, onResume
 * Behavior: Does not expand into the full stepper. Copy counts use rehostCounts when present.
 * Data Source: local (parent hook)
 * Constraints: no apiClient
 * Performance: one run document
 */
export function LatestDepartmentStatusChip({
  run,
  onViewReport,
  onResume,
}: {
  run: MigrationRunRow | null;
  onViewReport?: () => void;
  onResume?: () => void;
}) {
  if (!run?.steps?.length) return null;
  if (isDepartmentWideRunLive(run.status)) return null;

  const clearDatasetCount = run.steps.filter((step) => isDepartmentDatasetClear(step)).length;
  const outcome = describeRunOutcome(run);
  const hasUnfinishedSteps = (run.steps ?? []).some((step) => !isDepartmentDatasetClear(step));
  const canResume = run.status === 'cancelled' || run.status === 'failed' || (run.status === 'completed' && hasUnfinishedSteps);

  return (
    <section className="shrink-0 border-b bg-gray-50" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <div className="px-4 py-2 flex items-center gap-3">
        <div className="min-w-0 flex-1 text-xs text-gray-700">
          <span className="font-semibold text-gray-900">Latest department run</span>
          {' · '}
          {clearDatasetCount}/{run.steps.length} datasets
          {' · '}
          {outcome.headline}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canResume && onResume && (
            <button
              type="button"
              onClick={onResume}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-orange-200 text-orange-800 bg-white rounded-md hover:bg-orange-50"
            >
              Resume remaining
            </button>
          )}
          {onViewReport && (
            <button
              type="button"
              onClick={onViewReport}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-orange-200 text-orange-800 bg-white rounded-md hover:bg-orange-50"
            >
              View report
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * @component DatasetProgressStrip
 * Purpose: Live n/total for the open dataset tab while a per-tab apply is running.
 * Props: run, datasetLabel, onStop, onViewDepartmentReport
 * Behavior: Hidden unless the run is pending/running. Stop cancels that child run only.
 *           Stays visible beside a department sidebar so a tab run is not hidden by a stale department run.
 * Data Source: local (parent hook)
 * Constraints: no apiClient
 * Performance: one run document
 */
export function DatasetProgressStrip({
  run,
  datasetLabel,
  onStop,
  onResume,
  onViewDepartmentReport,
  onDismiss,
  stopping = false,
  resuming = false,
}: {
  run: MigrationRunRow | null;
  datasetLabel: string;
  onStop?: () => void;
  onResume?: () => void;
  onViewDepartmentReport?: () => void;
  onDismiss?: () => void;
  stopping?: boolean;
  resuming?: boolean;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isActivelyRunning = Boolean(run)
    && (isDepartmentWideRunLive(run?.status) || run?.rehostStatus === 'running');
  const isResumableTerminal = Boolean(run)
    && run?.mode === 'apply'
    && (run?.status === 'cancelled' || run?.status === 'failed');
  const visible = isActivelyRunning || isResumableTerminal;

  useEffect(() => {
    if (!isActivelyRunning) return;
    const id = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [isActivelyRunning]);

  if (!run || !visible) return null;

  const line = formatStepProgressLine({
    status: run.status,
    phase: run.phase,
    processed: run.processed,
    total: run.total,
    message: run.message,
    displayName: datasetLabel,
  });
  const percent = typeof run.overallPercent === 'number'
    ? run.overallPercent
    : run.total && run.total > 0
      ? Math.min(100, Math.round(((run.processed ?? 0) / run.total) * 100))
      : 0;
  const stale = isActivelyRunning && isStepHeartbeatStale(run.heartbeatAt ?? run.updatedAt, nowMs, undefined, run.startedAt);
  const copyLine = formatRehostProgressLine({
    rehostStatus: run.rehostStatus,
    rehostCounts: run.rehostCounts,
  });
  const stoppingNow = Boolean(stopping || run.cancelRequested) && isActivelyRunning;
  const canResume = run.mode === 'apply' && (isResumableTerminal || stale);
  const headingText = run.status === 'cancelled'
    ? `${datasetLabel} paused`
    : run.status === 'failed'
      ? `${datasetLabel} interrupted`
      : `Applying ${datasetLabel}`;

  return (
    <section className="shrink-0 border-b bg-orange-50/70" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <div className="px-4 py-2.5 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {run.status === 'failed' ? (
              <XCircle className="h-4 w-4 text-red-600 shrink-0" />
            ) : stale || run.status === 'cancelled' ? (
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            ) : (
              <Loader2 className="h-4 w-4 text-orange-600 animate-spin shrink-0" />
            )}
            <h2 className="text-sm font-semibold text-gray-900">This dataset · {headingText}</h2>
            <span className="text-xs text-gray-600">
              {percent}% · {run.mode === 'dry-run' ? 'Dry run' : 'Apply'}
              {stale ? ' · no recent progress' : run.status === 'cancelled' ? ' · stopped' : run.status === 'failed' ? ' · failed' : ''}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 rounded bg-white overflow-hidden border border-orange-100">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${Math.min(100, percent)}%` }} />
          </div>
          <p className="text-xs text-orange-800 mt-1.5 truncate">
            {line || run.message || `${datasetLabel} is processing…`}
            {copyLine ? ` · ${copyLine}` : ''}
          </p>
          {stale && (
            <p className="text-[11px] text-amber-800 mt-1">
              This migration has not reported progress for over 2 minutes. It may still be processing a large record. You can continue waiting, resume remaining records, or stop the migration.
            </p>
          )}
          {isResumableTerminal && (
            <p className="text-[11px] text-gray-700 mt-1">
              Already-migrated records are checkpointed. Dismiss this banner to start a new apply from the Grid tab, or Resume remaining to continue this run.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canResume && onResume && (
            <button
              type="button"
              onClick={onResume}
              disabled={resuming || stoppingNow}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-orange-300 text-orange-900 bg-white rounded-md hover:bg-orange-50 disabled:opacity-50"
            >
              {resuming ? 'Resuming dataset…' : 'Resume this dataset'}
            </button>
          )}
          {isResumableTerminal && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50"
            >
              Dismiss
            </button>
          )}
          {isActivelyRunning && onStop && (
            <button
              type="button"
              onClick={onStop}
              disabled={stoppingNow || resuming}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-red-200 text-red-700 bg-white rounded-md hover:bg-red-50 disabled:opacity-50"
            >
              {stoppingNow ? 'Stopping dataset…' : 'Stop this dataset'}
            </button>
          )}
          {onViewDepartmentReport && (
            <button
              type="button"
              onClick={onViewDepartmentReport}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium border border-orange-200 text-orange-800 bg-white rounded-md hover:bg-orange-50"
            >
              Latest department report
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
